import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { X, Mail } from 'lucide-react';
import { Input } from './ui/input';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const NewsletterPopup = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    // Check if user already dismissed or subscribed
    const dismissed = localStorage.getItem('newsletter_dismissed');
    const subscribed = localStorage.getItem('newsletter_subscribed');
    
    if (dismissed || subscribed) return;

    // Show popup after 5 seconds
    const timer = setTimeout(() => {
      setIsOpen(true);
    }, 5000);

    return () => clearTimeout(timer);
  }, []);

  const handleClose = () => {
    setIsOpen(false);
    localStorage.setItem('newsletter_dismissed', 'true');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      await axios.post(`${API_URL}/api/notify-emails`, {
        email: email,
        rally_id: null
      });
      
      toast.success("You're on the list! We'll keep you posted on upcoming rallies.");
      localStorage.setItem('newsletter_subscribed', 'true');
      setIsOpen(false);
    } catch (error) {
      if (error.response?.status === 409 || error.response?.data?.detail?.includes('duplicate')) {
        toast.success("You're already subscribed!");
        localStorage.setItem('newsletter_subscribed', 'true');
        setIsOpen(false);
      } else {
        toast.error('Something went wrong. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={handleClose}
            className="fixed inset-0 bg-black/60 z-50"
          />
          
          {/* Popup Container - Flexbox centered */}
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none">
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              data-testid="newsletter-popup"
              className="w-full max-w-md pointer-events-auto"
            >
            <div className="bg-[#141414] border border-[#2A2A2A] p-8 relative">
              {/* Close button */}
              <button
                onClick={handleClose}
                data-testid="newsletter-close"
                className="absolute top-4 right-4 text-[#A19D98] hover:text-[#F2EDE6] transition-colors"
              >
                <X size={20} />
              </button>

              {/* Content */}
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-[#E8651A]/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Mail className="w-8 h-8 text-[#E8651A]" />
                </div>
                <h2 className="font-['Oswald'] text-2xl text-[#F2EDE6] mb-2">
                  STAY IN THE LOOP
                </h2>
                <p className="text-[#A19D98] text-sm">
                  Get notified about upcoming rallies, exclusive events, and community news. No spam, just the good stuff.
                </p>
              </div>

              {/* Form */}
              <form onSubmit={handleSubmit} className="space-y-4">
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  required
                  data-testid="newsletter-email"
                  className="bg-[#0A0A0A] border-[#2A2A2A] text-[#F2EDE6] text-center"
                />
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="newsletter-submit"
                  className="btn-primary w-full disabled:opacity-50"
                >
                  {submitting ? 'Subscribing...' : 'Join the Rally Family'}
                </button>
              </form>

              <p className="text-[#A19D98] text-xs text-center mt-4">
                Unsubscribe anytime. We respect your inbox.
              </p>
            </div>
          </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );
};

export default NewsletterPopup;
